import type { User } from "firebase/auth";
import {
  KillStreakTracker,
  type KillStreakState,
} from "@/game/model/kill-streak";
import { HomeEvents } from "@/home/home-events";
import {
  buyReviveCardMock,
  createDefaultHomeEconomy,
  REVIVE_CARD_CASH_COST,
  setItemHotbarSlot,
  setItemHotbarSlotAuto,
  setCombatHpCurrent,
  shouldPersistCombatHp,
  useHealthPotion,
  useReviveCard,
  type HomeEconomy,
} from "@/home/economy";
import { tierFromStashItemId } from "@/home/health-potions";
import {
  bindItemHotbar,
  hotbarUseBlockedReason,
  renderItemHotbarHtml,
  syncItemHotbar,
  syncItemHotbarCooldownState,
} from "@/home/home-item-hotbar";
import { playSkillBarRowMarkup } from "@/home/home-play-skill-bar";
import {
  itemHotbarAutoUseReady,
  itemHotbarUseReady,
  markItemHotbarAutoUsed,
  markItemHotbarUsed,
  pickAutoHotbarPotionSlot,
  shouldAutoUseHotbarPotion,
} from "@/home/item-hotbar-use";
import type { StashItemId } from "@/home/home-stash-modal";
import { loadHomeEconomyFromIdb, saveHomeEconomyToIdb } from "@/home/home-idb";
import { mountTowerSkillBar, type TowerSkillBarHandle } from "@/tower/tower-skill-bar";
import {
  TowerEvents,
  type TowerPlayerHpChangedPayload,
} from "@/tower/tower-events";

export function towerCombatBarMarkup(): string {
  return `
    <div class="tower-combat-bar" id="tower-combat-bar" hidden>
      <div class="tower-combat-bar-actions" aria-label="ควบคุมสนามรบ">
        <div
          class="tower-kill-streak"
          id="tower-kill-streak"
          title="Hit ต่อเนื่องในสนามรบ · ห่างเกิน 10 วินาทีแล้วรีเซ็ต · รีเฟรชหน้าจบ combo ปัจจุบัน"
          aria-live="polite"
        >
          <span class="tower-kill-streak-label">Hit</span>
          <span class="tower-kill-streak-value" id="tower-kill-streak-current">0</span>
          <span class="tower-kill-streak-best">
            สูงสุด <span id="tower-kill-streak-best">0</span>
          </span>
        </div>
        <button
          type="button"
          class="tower-surrender-btn"
          id="tower-surrender-btn"
          title="ยอมแพ้"
          disabled
        >
          ยอมแพ้
        </button>
        <button
          type="button"
          class="tower-auto-btn"
          id="tower-auto-btn"
          title="วิ่งไล่มอนสเตอร์และตีอัตโนมัติเมื่อเข้าใกล้"
          disabled
        >
          Auto
        </button>
      </div>
      <div class="home-combat-hotbar tower-combat-hotbar">
        ${playSkillBarRowMarkup("tower-skill-bar", "tower-skill-auto-all", "tower-skill-bar")}
        ${renderItemHotbarHtml("tower")}
      </div>
    </div>
  `;
}

export function towerReviveModalMarkup(): string {
  return `
    <div id="tower-revive-modal" class="tower-revive-modal" hidden aria-hidden="true">
      <button type="button" class="tower-revive-backdrop" id="tower-revive-backdrop" aria-label="ปิด"></button>
      <div class="tower-revive-dialog" role="dialog" aria-modal="true" aria-labelledby="tower-revive-title">
        <h2 id="tower-revive-title" class="tower-revive-title">คุณล้มในสนามรบ</h2>
        <p class="tower-revive-lede">ต้องการชุบชีวิตเพื่อสู้ต่อหรือยอมแพ้?</p>
        <p class="hint tower-revive-meta">บัตรชุบในคลัง: <strong id="tower-revive-card-count">0</strong> ใบ</p>
        <p class="hint tower-revive-status" id="tower-revive-status" role="status"></p>
        <div class="tower-revive-actions">
          <button type="button" class="btn btn-primary" id="tower-revive-use-card">ชุบชีวิต (ใช้บัตร 1 ใบ)</button>
          <button type="button" class="btn btn-ghost" id="tower-revive-give-up">ยอมแพ้</button>
          <div class="tower-revive-buy-row">
            <p class="hint">ซื้อบัตรชุบ: ${REVIVE_CARD_CASH_COST.toLocaleString("en-US")} cash · คุณมี <span id="tower-revive-cash">0</span> cash</p>
            <button type="button" class="btn btn-ghost btn-sm" id="tower-revive-buy-card">ซื้อบัตรชุบ</button>
            <a class="hint text-link" href="/home?shop=revive">หรือซื้อที่บ้าน · ร้าน</a>
          </div>
        </div>
      </div>
    </div>
  `;
}

export type TowerCombatOverlayOptions = {
  isBattleActive: () => boolean;
  surrenderConfirm?: string;
  onCombatHpChange?: (current: number) => void;
  persistEconomy?: boolean;
  enableRevive?: boolean;
};

export type TowerCombatOverlayHandle = {
  showBar: () => void;
  hideBar: () => void;
  onBattleStart: () => void;
  onBattleEnd: () => void;
  getSkillRuntime: () => ReturnType<TowerSkillBarHandle["getRuntime"]>;
  destroy: () => void;
};

export async function mountTowerCombatOverlay(
  root: HTMLElement,
  user: User,
  options: TowerCombatOverlayOptions,
): Promise<TowerCombatOverlayHandle> {
  let economy: HomeEconomy =
    (await loadHomeEconomyFromIdb(user.uid)) ?? createDefaultHomeEconomy();
  let playerCombatHp = { current: 0, max: 1 };
  let playerDowned = false;
  let autoChaseOn = false;
  let killStreakTracker: KillStreakTracker | null = null;

  const persistEconomy = () => {
    if (!options.persistEconomy) return;
    void saveHomeEconomyToIdb(user.uid, economy);
    window.dispatchEvent(
      new CustomEvent(HomeEvents.EconomyChanged, { detail: { economy } }),
    );
  };

  const skillBarHud = await mountTowerSkillBar(root, user.uid, options.isBattleActive);

  const syncItemHotbarHud = () => {
    syncItemHotbar(root, economy, "tower", {
      disabled: !options.isBattleActive() || playerDowned,
      itemUseCooldown: !itemHotbarUseReady(),
    });
    syncItemHotbarCooldownState(root, "tower");
  };

  const applyHotbarPotion = (
    itemId: StashItemId,
    opts?: { silent?: boolean; skipCooldown?: boolean },
  ): boolean => {
    if (!options.isBattleActive() || playerDowned) return false;
    if (!opts?.skipCooldown && !itemHotbarUseReady()) {
      if (!opts?.silent) syncItemHotbarHud();
      return false;
    }
    if (hotbarUseBlockedReason(itemId)) return false;
    const tier = tierFromStashItemId(itemId);
    if (!tier) return false;
    const result = useHealthPotion(economy, tier, playerCombatHp);
    if (!result.ok) return false;
    economy = result.economy;
    if (!opts?.skipCooldown) markItemHotbarUsed();
    playerCombatHp = {
      current: Math.min(playerCombatHp.max, playerCombatHp.current + result.healed),
      max: playerCombatHp.max,
    };
    if (
      options.persistEconomy &&
      shouldPersistCombatHp(economy, playerCombatHp.current, playerCombatHp.max)
    ) {
      economy = setCombatHpCurrent(economy, playerCombatHp.current);
    }
    persistEconomy();
    window.dispatchEvent(
      new CustomEvent(TowerEvents.HealthPotionHeal, {
        detail: { amount: result.healed },
      }),
    );
    syncItemHotbarHud();
    return result.healed > 0;
  };

  const tryAutoItemHotbar = () => {
    if (!options.isBattleActive() || playerDowned) return;
    if (!itemHotbarAutoUseReady()) return;
    const { current, max } = playerCombatHp;
    if (!shouldAutoUseHotbarPotion(current, max, economy.itemHotbarAutoHpPercent)) return;
    const pick = pickAutoHotbarPotionSlot(economy);
    if (!pick) return;
    if (applyHotbarPotion(pick.itemId, { silent: true, skipCooldown: true })) {
      markItemHotbarAutoUsed();
    }
  };

  const unbindItemHotbar = bindItemHotbar(root, {
    prefix: "tower",
    getEconomy: () => economy,
    onAssign: (index, itemId) => {
      economy = setItemHotbarSlot(economy, index, itemId);
      persistEconomy();
      syncItemHotbarHud();
    },
    onUseItem: (itemId) => {
      applyHotbarPotion(itemId);
    },
    onSlotAutoChange: (index, enabled) => {
      economy = setItemHotbarSlotAuto(economy, index, enabled);
      persistEconomy();
      syncItemHotbarHud();
    },
    isUseBlocked: () => !options.isBattleActive() || playerDowned,
  });

  const itemHotbarAutoTimer = setInterval(() => {
    syncItemHotbarHud();
    tryAutoItemHotbar();
  }, 250);

  const syncAutoButton = () => {
    const btn = root.querySelector<HTMLButtonElement>("#tower-auto-btn");
    if (!btn) return;
    const active = options.isBattleActive();
    btn.disabled = !active;
    btn.classList.toggle("tower-auto-btn--on", autoChaseOn && active);
    btn.setAttribute("aria-pressed", autoChaseOn && active ? "true" : "false");
  };

  const syncSurrenderButton = () => {
    const btn = root.querySelector<HTMLButtonElement>("#tower-surrender-btn");
    if (!btn) return;
    btn.disabled = !options.isBattleActive();
  };

  const syncKillStreakHud = (state: KillStreakState) => {
    const currentEl = root.querySelector<HTMLElement>("#tower-kill-streak-current");
    const bestEl = root.querySelector<HTMLElement>("#tower-kill-streak-best");
    const wrap = root.querySelector<HTMLElement>("#tower-kill-streak");
    if (currentEl) currentEl.textContent = String(state.current);
    if (bestEl) bestEl.textContent = String(state.best);
    wrap?.classList.toggle("tower-kill-streak--active", state.current > 0);
  };

  const stopKillStreakTracker = () => {
    killStreakTracker?.destroy();
    killStreakTracker = null;
  };

  const startKillStreakTracker = () => {
    stopKillStreakTracker();
    killStreakTracker = new KillStreakTracker(user.uid, syncKillStreakHud);
  };

  const setReviveStatus = (message: string, isError = false) => {
    const el = root.querySelector<HTMLElement>("#tower-revive-status");
    if (!el) return;
    el.textContent = message;
    el.classList.toggle("tower-revive-status--error", isError);
  };

  const syncReviveModalUi = () => {
    const countEl = root.querySelector<HTMLElement>("#tower-revive-card-count");
    const cashEl = root.querySelector<HTMLElement>("#tower-revive-cash");
    const useBtn = root.querySelector<HTMLButtonElement>("#tower-revive-use-card");
    const buyBtn = root.querySelector<HTMLButtonElement>("#tower-revive-buy-card");
    if (countEl) countEl.textContent = String(economy.wallet.reviveCards);
    if (cashEl) cashEl.textContent = economy.wallet.cash.toLocaleString("en-US");
    if (useBtn) useBtn.disabled = economy.wallet.reviveCards <= 0;
    if (buyBtn) buyBtn.disabled = economy.wallet.cash < REVIVE_CARD_CASH_COST;
  };

  const openReviveModal = () => {
    if (!options.enableRevive) {
      window.dispatchEvent(new CustomEvent(TowerEvents.BattleGiveUp));
      return;
    }
    const modal = root.querySelector<HTMLElement>("#tower-revive-modal");
    if (!modal) return;
    setReviveStatus("");
    syncReviveModalUi();
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
  };

  const closeReviveModal = () => {
    const modal = root.querySelector<HTMLElement>("#tower-revive-modal");
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    setReviveStatus("");
  };

  const setAutoChase = (enabled: boolean) => {
    autoChaseOn = enabled;
    syncAutoButton();
    if (options.isBattleActive()) {
      window.dispatchEvent(
        new CustomEvent(TowerEvents.AutoChaseChanged, { detail: { enabled: autoChaseOn } }),
      );
    }
  };

  const showBar = () => {
    const bar = root.querySelector<HTMLElement>("#tower-combat-bar");
    if (bar) bar.hidden = false;
  };

  const hideBar = () => {
    const bar = root.querySelector<HTMLElement>("#tower-combat-bar");
    if (bar) bar.hidden = true;
  };

  const onBattleEnd = () => {
    if (autoChaseOn) {
      autoChaseOn = false;
      window.dispatchEvent(
        new CustomEvent(TowerEvents.AutoChaseChanged, { detail: { enabled: false } }),
      );
    }
    playerDowned = false;
    closeReviveModal();
    stopKillStreakTracker();
    hideBar();
    syncAutoButton();
    syncSurrenderButton();
    skillBarHud.refresh();
    syncItemHotbarHud();
  };

  const onBattleStart = () => {
    playerDowned = false;
    showBar();
    skillBarHud.refresh();
    skillBarHud.syncMasterAutoCombat();
    syncItemHotbarHud();
    syncAutoButton();
    syncSurrenderButton();
    startKillStreakTracker();
  };

  const onClick = (event: Event) => {
    const target = event.target as HTMLElement;
    if (target.id === "tower-auto-btn" || target.closest("#tower-auto-btn")) {
      if (!options.isBattleActive()) return;
      setAutoChase(!autoChaseOn);
      return;
    }
    if (target.id === "tower-surrender-btn" || target.closest("#tower-surrender-btn")) {
      if (!options.isBattleActive()) return;
      const msg = options.surrenderConfirm ?? "ยอมแพ้?";
      if (!window.confirm(msg)) return;
      window.dispatchEvent(new CustomEvent(TowerEvents.BattleGiveUp));
      return;
    }
    if (target.id === "tower-revive-use-card" || target.closest("#tower-revive-use-card")) {
      const used = useReviveCard(economy);
      if (!used.ok) {
        setReviveStatus(used.reason, true);
        syncReviveModalUi();
        return;
      }
      economy = used.economy;
      persistEconomy();
      closeReviveModal();
      playerDowned = false;
      syncItemHotbarHud();
      window.dispatchEvent(new CustomEvent(TowerEvents.ReviveAccepted));
      return;
    }
    if (target.id === "tower-revive-give-up" || target.closest("#tower-revive-give-up")) {
      closeReviveModal();
      window.dispatchEvent(new CustomEvent(TowerEvents.BattleGiveUp));
      return;
    }
    if (target.id === "tower-revive-buy-card" || target.closest("#tower-revive-buy-card")) {
      const bought = buyReviveCardMock(economy);
      if (!bought.ok) {
        setReviveStatus(bought.reason, true);
        syncReviveModalUi();
        return;
      }
      economy = bought.economy;
      persistEconomy();
      setReviveStatus(`ซื้อบัตรชุบแล้ว · ใช้ ${bought.cashSpent} cash`);
      syncReviveModalUi();
      return;
    }
    if (target.id === "tower-revive-backdrop" || target.closest("#tower-revive-backdrop")) {
      closeReviveModal();
    }
  };

  const onPlayerHpChanged = (event: Event) => {
    const detail = (event as CustomEvent<TowerPlayerHpChangedPayload>).detail;
    if (!detail || !options.isBattleActive()) return;
    playerCombatHp = { current: detail.current, max: detail.max };
    if (
      options.persistEconomy &&
      shouldPersistCombatHp(economy, detail.current, detail.max)
    ) {
      const next = setCombatHpCurrent(economy, detail.current);
      if (next !== economy) {
        economy = next;
        persistEconomy();
      }
    }
    options.onCombatHpChange?.(detail.current);
    syncItemHotbarHud();
    tryAutoItemHotbar();
  };

  const onPlayerDowned = () => {
    if (!options.isBattleActive()) return;
    playerDowned = true;
    syncItemHotbarHud();
    openReviveModal();
  };

  const onReviveAccepted = () => {
    playerDowned = false;
    syncItemHotbarHud();
  };

  const onEnemyDefeated = () => {
    killStreakTracker?.recordKill();
  };

  const onEconomyChanged = (event: Event) => {
    const detail = (event as CustomEvent<{ economy: HomeEconomy }>).detail;
    if (!detail?.economy) return;
    economy = detail.economy;
    syncReviveModalUi();
    syncItemHotbarHud();
  };

  root.addEventListener("click", onClick);
  window.addEventListener(TowerEvents.PlayerHpChanged, onPlayerHpChanged);
  window.addEventListener(TowerEvents.PlayerDowned, onPlayerDowned);
  window.addEventListener(TowerEvents.ReviveAccepted, onReviveAccepted);
  window.addEventListener(TowerEvents.EnemyDefeated, onEnemyDefeated);
  window.addEventListener(HomeEvents.EconomyChanged, onEconomyChanged);

  syncAutoButton();
  syncSurrenderButton();
  syncItemHotbarHud();

  return {
    showBar,
    hideBar,
    onBattleStart,
    onBattleEnd,
    getSkillRuntime: () => skillBarHud.getRuntime(),
    destroy: () => {
      root.removeEventListener("click", onClick);
      window.removeEventListener(TowerEvents.PlayerHpChanged, onPlayerHpChanged);
      window.removeEventListener(TowerEvents.PlayerDowned, onPlayerDowned);
      window.removeEventListener(TowerEvents.ReviveAccepted, onReviveAccepted);
      window.removeEventListener(TowerEvents.EnemyDefeated, onEnemyDefeated);
      window.removeEventListener(HomeEvents.EconomyChanged, onEconomyChanged);
      clearInterval(itemHotbarAutoTimer);
      unbindItemHotbar();
      onBattleEnd();
      if (options.persistEconomy) {
        persistEconomy();
      }
      skillBarHud.destroy();
    },
  };
}
